import { isOptionProductFields } from '@src/types/OTC/otcGuards';
import type { DealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import {
  type ExerciseStyleEnum,
  logger,
  type OTCOptionProductTemplate,
  type OTCSettlementTypeEnum,
  type PayoffTypeEnum,
  runValidationV1WithResult,
  StrikeTypeEnum,
  type StrikeTypeEnum as StrikeTypeEnumType,
  SubProductTypeEnum,
} from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { BaseProductDefinition, type CreateProductParams } from './BaseProductDefinition';
import type { CreateVanillaOptionRequest, VanillaOptionProductDetails } from './CustomInstrumentTypes';
import { optionProductBuilderSchema, type ValidatedOptionRowData } from './OptionProductBuilderSliceStrategy';

export class OptionProductDefinition extends BaseProductDefinition {
  private otcOptionProductTemplatesByDisplaySymbol: Map<string, OTCOptionProductTemplate> = new Map();
  private enableOTCDeltaStrikeSupport = false;

  updateReferenceData(otcOptionProductTemplatesByDisplaySymbol: Map<string, OTCOptionProductTemplate>) {
    this.otcOptionProductTemplatesByDisplaySymbol = otcOptionProductTemplatesByDisplaySymbol;
  }

  setEnableOTCDeltaStrikeSupport(enabled: boolean): void {
    this.enableOTCDeltaStrikeSupport = enabled;
  }

  buildPostBody(row: ValidatedOptionRowData): CreateVanillaOptionRequest | undefined {
    const validationRun = runValidationV1WithResult(optionProductBuilderSchema, row);
    if (validationRun.result === 'failure') {
      return;
    }
    const validatedData = validationRun.validatedData;

    const baseAsset = validatedData.baseAsset;
    const deliveryAsset =
      validatedData.otcFields.payoffType === 'Inverse'
        ? validatedData.baseAsset
        : validatedData.otcFields.deliveryAsset || validatedData.otcFields.quoteAsset;

    // Index is required and should be provided from formData
    const index = validatedData.otcFields.index;

    const strikeType = validatedData.otcFields.strikeType as StrikeTypeEnumType;
    const strikeValue = validatedData.otcFields.strikePrice || undefined;
    const useStrikeDeltaForDelta = this.enableOTCDeltaStrikeSupport && strikeType === StrikeTypeEnum.Delta;

    const productDetails: VanillaOptionProductDetails = {
      PayoffType: validatedData.otcFields.payoffType as PayoffTypeEnum,
      SettlementType: validatedData.otcFields.settlementType as OTCSettlementTypeEnum,
      Index: index,
      BaseAsset: baseAsset,
      QuoteAsset: validatedData.otcFields.payoffType === 'Inverse' ? baseAsset : validatedData.otcFields.quoteAsset,
      DeliveryAsset: deliveryAsset,
      UnitNotionalAmount: validatedData.otcFields.unitNotionalAmount,
      OptionType: validatedData.optionType,
      ExerciseStyle: validatedData.otcFields.exerciseStyle as ExerciseStyleEnum,
      StrikeType: strikeType,
      ...(useStrikeDeltaForDelta ? { StrikeDelta: strikeValue } : { StrikePrice: strikeValue, StrikePriceCcy: 'USD' }),
      PaymentCcy: validatedData.otcFields.paymentCcy,
      Tenor: validatedData.otcFields.tenor,
      ...(validatedData.uniqueSuffix && { UniqueSuffix: validatedData.uniqueSuffix }),
    };

    const result: CreateVanillaOptionRequest = {
      ProductType: 'OTC',
      SubProductType: SubProductTypeEnum.OTCVanillaOption,
      RequestType: 'OTC',
      ProductDetails: productDetails,
    };
    return result;
  }

  getProductDetailsForComparison(row: DealProductBuilderRow): CreateVanillaOptionRequest | null {
    if (!row.otcFields || !row.baseAsset || !isOptionProductFields(row.otcFields)) {
      return null;
    }

    const validation = runValidationV1WithResult(optionProductBuilderSchema, row);
    if (validation.result === 'failure') {
      throw new Error('Product builder validation failed');
    }

    return this.buildPostBody(validation.validatedData) || null;
  }

  checkForUniqueness(
    rowData: DealProductBuilderRow & { product: 'option' }
  ): 'notReady' | 'uncreated' | 'conflict' | 'alreadyCreated' {
    const validation = runValidationV1WithResult(optionProductBuilderSchema, rowData);
    if (validation.result === 'failure') {
      return 'notReady';
    }

    const productDetails = this.getProductDetailsForComparison(rowData);
    if (!productDetails) {
      return 'notReady';
    }

    const postBody = this.buildPostBody(validation.validatedData);
    if (!postBody) {
      return 'notReady';
    }
    const symbol =
      validation.validatedData.productTemplate +
      (postBody.ProductDetails.UniqueSuffix ? `-${postBody.ProductDetails.UniqueSuffix}` : '');

    const existingSecurity = this.otcOptionProductTemplatesByDisplaySymbol.get(symbol);
    if (!existingSecurity) {
      return 'uncreated';
    }

    const existingProductDetails = existingSecurity?.ProductDetails;
    if (!isEqual(postBody.ProductDetails, existingProductDetails)) {
      return 'conflict';
    }

    return 'alreadyCreated';
  }

  async createProduct({
    rowData,
    createCustomInstrument,
    onSuccess,
    onError,
  }: CreateProductParams & {
    rowData: DealProductBuilderRow & { product: 'option' };
  }): Promise<{ symbol: string } | undefined> {
    const validation = runValidationV1WithResult(optionProductBuilderSchema, rowData);
    if (validation.result === 'failure') {
      logger.warn(`Failed to validate OTC Option: ${JSON.stringify(validation.errors)}`);
      onError('Validation failed on OTC Option');
      return;
    }

    try {
      this.checkForUniqueness(rowData);
      const option = this.buildPostBody(validation.validatedData);
      if (!option) {
        onError('Failed to build option product body');
        return;
      }

      const rows = await createCustomInstrument(option);
      onSuccess('Option product created successfully');
      logger.info('Option product created successfully', { data: rows });

      return { symbol: rows[0]?.Symbol ?? '' };
    } catch (error) {
      if (error instanceof Error && error.name === 'ValidationError') {
        onError(`Validation failed: ${error.message}`);
      } else {
        const serverMessage = this.getServerErrorMessage(error);
        onError(serverMessage ? `Error creating option product: ${serverMessage}` : 'Error creating option product');
      }
      logger.error(new Error('Error creating option product'), {
        extra: {
          input: { rowData },
          message: error instanceof Error ? error.message : 'Unknown error',
          error,
        },
      });
      return;
    }
  }
}
